import React, { useState } from "react";

import { API_BASE as API } from "../utils/apiBase";

// Reusable TOTP enrollment flow: start -> scan QR + confirm a code -> show one-time recovery codes.
// Used both in Profile settings and as the forced enrollment screen for admins (pass mandatory).
export default function MfaEnroll({ token, onComplete, onCancel, mandatory = false }) {
  const [step, setStep] = useState("intro"); // intro | scan | done
  const [setup, setSetup] = useState(null); // { secret, otpauth_uri, qr_data_uri }
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const start = async () => {
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/mfa/enroll/start`, { method: "POST", headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Could not start setup. Please try again.");
      setSetup(data);
      setStep("scan");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/mfa/enroll/confirm`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "That code did not match. Try again.");
      setRecovery(Array.isArray(data.recovery_codes) ? data.recovery_codes : []);
      setStep("done");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm tracking-widest";

  return (
    <div className="space-y-4 text-gray-800 dark:text-gray-100">
      {error ? (
        <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      ) : null}

      {step === "intro" ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Add a second step at login using an authenticator app (Google Authenticator, Authy,
            1Password, etc.). You will scan a QR code, enter a 6-digit code to confirm, and save a set
            of one-time recovery codes.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={start}
              disabled={busy}
              className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60"
            >
              {busy ? "Starting..." : "Set up authenticator"}
            </button>
            {!mandatory && onCancel ? (
              <button
                type="button"
                onClick={onCancel}
                className="rounded-xl border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {step === "scan" && setup ? (
        <form onSubmit={confirm} className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Scan this QR code in your authenticator app, then enter the 6-digit code it shows.
          </p>
          {setup.qr_data_uri ? (
            <img
              src={setup.qr_data_uri}
              alt="Two-factor QR code"
              width={180}
              height={180}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white p-2"
            />
          ) : null}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Can't scan? Enter this key manually:{" "}
            <code className="rounded bg-gray-100 dark:bg-gray-800 px-1 py-0.5 break-all">{setup.secret}</code>
          </p>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={code}
            onChange={(ev) => setCode(ev.target.value)}
            className={inputCls}
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy || code.trim().length < 6}
              className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60"
            >
              {busy ? "Verifying..." : "Verify and enable"}
            </button>
            {!mandatory && onCancel ? (
              <button
                type="button"
                onClick={onCancel}
                className="rounded-xl border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      ) : null}

      {step === "done" ? (
        <div className="space-y-3">
          <div className="rounded-lg border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/40 px-3 py-2 text-sm text-green-800 dark:text-green-300">
            Two-factor authentication is on. Save these one-time recovery codes somewhere safe - each
            works once if you ever lose your authenticator.
          </div>
          <div className="grid grid-cols-2 gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 font-mono text-sm">
            {recovery.map((c) => (
              <div key={c} className="select-all">{c}</div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => onComplete && onComplete()}
            className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
          >
            I saved my recovery codes - continue
          </button>
        </div>
      ) : null}
    </div>
  );
}
